"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var RecursionResult_1 = require("./RecursionResult");
var isObject = function (v) { return typeof v === 'object' && v != null; };
/**
 * Creates an iterable for recursively accessing an object heirarchy.
 * @export
 * @class RecursionBuilder
 * @implements {Iterable<[ K, V, string, O ]>}
 * @template K The type of the key.
 * @template V The type of the value.
 * @template O The type of the data object.
 */
var RecursionBuilder = (function () {
    /**
     * Creates an instance of RecursionBuilder.
     * @param {O} [_object]
     * @param {RecursionBuilderState<K, V, O>} [state={}]
     */
    function RecursionBuilder(_object, state) {
        if (state === void 0) { state = {}; }
        this._object = _object;
        this._yieldOn = state.yieldOn || (function () { return true; });
        this._traverseOn = state.traverseOn || isObject;
        this._readEntry = state.readEntry || objectEntryReader;
        this._readNext = state.readNext;
    }
    /**
     * Sets the yield filter and returns a new RecursionBuilder. Determines whether
     * to yield the value.
     * @param {(value: V, key: K, object: O) => boolean} fn
     * @returns {RecursionBuilder<K, V, O>}
     */
    RecursionBuilder.prototype.yieldOn = function (fn) {
        return this.clone({ yieldOn: fn });
    };
    /**
     * Sets the traverse filter and returns a new RecursionBuilder. Determines whether
     * to traverse the value given.
     * @param {(value: V, key: K, object: O) => boolean} fn
     * @returns {RecursionBuilder<K, V, O>}
     */
    RecursionBuilder.prototype.traverseOn = function (fn) {
        return this.clone({ traverseOn: fn });
    };
    /**
     * Sets the read entry function and returns a new RecursionBuilder. This function
     * generates the key/value pairs of each key.
     * @param {(object: O) => Iterable<[ K, V ]>} fn
     * @returns {RecursionBuilder<K, V, O>}
     */
    RecursionBuilder.prototype.readEntry = function (fn) {
        return this.clone({ readEntry: fn });
    };
    /**
     * Sets the read next function and returns a new RecursionBuilder. This function
     * determines what value to read next. Useful for iterarting recursive arrays.
     *
     * @param {(((object: O) => O)|string)} fn
     * @returns {RecursionBuilder<K, V, O>}
     */
    RecursionBuilder.prototype.readNext = function (fn) {
        return this.clone({ readNext: createChildExtractor(fn) });
    };
    /**
     * Recurses the data object using the configured recursion algorithm.
     * @param {O} [object]
     * @param {string} [_path]
     * @returns {IterableIterator<[ K, V, string, O ]>}
     */
    RecursionBuilder.prototype.recurse = function (object) {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!object) {
                        return [2 /*return*/];
                    }
                    return [5 /*yield**/, tslib_1.__values(this._recurse(object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    /**
     * Clones this recursion builder instance.
     * @param {RecursionBuilderState<K, V, O>} [state={}]
     * @returns {RecursionBuilder<K, V, O>}
     */
    RecursionBuilder.prototype.clone = function (state) {
        if (state === void 0) { state = {}; }
        return RecursionBuilder.create(this._object, tslib_1.__assign({ yieldOn: this._yieldOn, traverseOn: this._traverseOn, readEntry: this._readEntry, readNext: this._readNext }, state));
    };
    RecursionBuilder.prototype[Symbol.iterator] = function () {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [5 /*yield**/, tslib_1.__values(this.recurse(this._object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    /**
     * Iterates and returns only value results.
     * @param {O} [object]
     * @returns {IterableIterator<V>}
     */
    RecursionBuilder.prototype.values = function (object) {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [5 /*yield**/, tslib_1.__values(this.extract('value', object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    /**
     * Iterates and returns only keys.
     * @param {O} [object]
     * @returns {IterableIterator<K>}
     */
    RecursionBuilder.prototype.keys = function (object) {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [5 /*yield**/, tslib_1.__values(this.extract('key', object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    /**
     * Iterates and returns only paths.
     * @param {O} [object]
     * @returns {IterableIterator<string>}
     */
    RecursionBuilder.prototype.paths = function (object) {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [5 /*yield**/, tslib_1.__values(this.extract('path', object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    /**
     * Iterates and returns only parent objects.
     * @param {O} [object]
     * @returns {IterableIterator<string>}
     */
    RecursionBuilder.prototype.parents = function (object) {
        return tslib_1.__generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [5 /*yield**/, tslib_1.__values(this.extract('parent', object))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    };
    RecursionBuilder.prototype.extract = function (key, object) {
        var _a, _b, results, e_1_1, e_1, _c;
        return tslib_1.__generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    _d.trys.push([0, 5, 6, 7]);
                    _a = tslib_1.__values(this.recurse(object || this._object)), _b = _a.next();
                    _d.label = 1;
                case 1:
                    if (!!_b.done) return [3 /*break*/, 4];
                    results = _b.value;
                    return [4 /*yield*/, results[key]];
                case 2:
                    _d.sent();
                    _d.label = 3;
                case 3:
                    _b = _a.next();
                    return [3 /*break*/, 1];
                case 4: return [3 /*break*/, 7];
                case 5:
                    e_1_1 = _d.sent();
                    e_1 = { error: e_1_1 };
                    return [3 /*break*/, 7];
                case 6:
                    try {
                        if (_b && !_b.done && (_c = _a.return)) _c.call(_a);
                    }
                    finally { if (e_1) throw e_1.error; }
                    return [7 /*endfinally*/];
                case 7: return [2 /*return*/];
            }
        });
    };
    /**
     * Recurses the data object using the configured recursion algorithm.
     * @param {O} [object]
     * @param {string} [_path]
     * @returns {IterableIterator<[ K, V, string, O ]>}
     */
    RecursionBuilder.prototype._recurse = function (object, _path, lastResult) {
        var _a, _b, _c, key, value, path, nextValue, result, e_2_1, e_2, _d;
        return tslib_1.__generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    _e.trys.push([0, 7, 8, 9]);
                    _a = tslib_1.__values(this._readEntry(object)), _b = _a.next();
                    _e.label = 1;
                case 1:
                    if (!!_b.done) return [3 /*break*/, 6];
                    _c = tslib_1.__read(_b.value, 2), key = _c[0], value = _c[1];
                    path = resolvePath(_path, key, object);
                    nextValue = value;
                    result = lastResult ? lastResult : undefined;
                    if (!this._yieldOn(value, key, object)) return [3 /*break*/, 3];
                    result = new RecursionResult_1.RecursionResult(value, key, path, object, lastResult ? lastResult : null);
                    return [4 /*yield*/, result];
                case 2:
                    _e.sent();
                    _e.label = 3;
                case 3:
                    if (this._readNext) {
                        nextValue = this._readNext(value);
                    }
                    if (!this._traverseOn(nextValue, key, object)) return [3 /*break*/, 5];
                    return [5 /*yield**/, tslib_1.__values(this._recurse(nextValue, path, result))];
                case 4:
                    _e.sent();
                    _e.label = 5;
                case 5:
                    _b = _a.next();
                    return [3 /*break*/, 1];
                case 6: return [3 /*break*/, 9];
                case 7:
                    e_2_1 = _e.sent();
                    e_2 = { error: e_2_1 };
                    return [3 /*break*/, 9];
                case 8:
                    try {
                        if (_b && !_b.done && (_d = _a.return)) _d.call(_a);
                    }
                    finally { if (e_2) throw e_2.error; }
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    };
    RecursionBuilder.create = function (object, state) {
        return new RecursionBuilder(object, state);
    };
    return RecursionBuilder;
}());
exports.RecursionBuilder = RecursionBuilder;
function createChildExtractor(fn) {
    return function (value) {
        return typeof fn === 'string' ? isObject(value) ? value[fn] : undefined : fn(value);
    };
}
function resolvePath(path, key, value) {
    return path ? Array.isArray(value) ? path + "[" + key + "]" : path + "." + key : key.toString();
}
function objectEntryReader(object) {
    var _a, _b, key, e_3_1, e_3, _c;
    return tslib_1.__generator(this, function (_d) {
        switch (_d.label) {
            case 0:
                if (!(typeof object.entries === 'function')) return [3 /*break*/, 2];
                return [5 /*yield**/, tslib_1.__values(object.entries())];
            case 1:
                _d.sent();
                return [3 /*break*/, 9];
            case 2:
                _d.trys.push([2, 7, 8, 9]);
                _a = tslib_1.__values(Object.keys(object)), _b = _a.next();
                _d.label = 3;
            case 3:
                if (!!_b.done) return [3 /*break*/, 6];
                key = _b.value;
                return [4 /*yield*/, [key, object[key]]];
            case 4:
                _d.sent();
                _d.label = 5;
            case 5:
                _b = _a.next();
                return [3 /*break*/, 3];
            case 6: return [3 /*break*/, 9];
            case 7:
                e_3_1 = _d.sent();
                e_3 = { error: e_3_1 };
                return [3 /*break*/, 9];
            case 8:
                try {
                    if (_b && !_b.done && (_c = _a.return)) _c.call(_a);
                }
                finally { if (e_3) throw e_3.error; }
                return [7 /*endfinally*/];
            case 9: return [2 /*return*/];
        }
    });
}
//# sourceMappingURL=RecursionBuilder.js.map